// src/features/clients/components/ShareReferralCard.tsx
// ============================================================================
// TARJETA DE INVITACIÓN COMPARTIBLE (referidos)
// ============================================================================
//
// Botón + modal que genera una imagen de invitación (fondo IA Gemini + texto
// por canvas, ver utils/referralCard.ts) para que el dueño la comparta por
// WhatsApp en nombre de un cliente/influencer. El amigo se registra EN LA TIENDA
// y menciona el código (o teléfono) → ambos ganan.
//
// Compatible con el modelo kiosco: NO es un link de auto-registro; es material
// promocional que el referido lleva a caja.
// ============================================================================

import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { renderReferralCard, ReferralCardVariant } from '../../../utils/referralCard';
import { toast } from 'react-hot-toast';
import { Share2, Download, Copy, Loader2, X, Check, AlertTriangle } from 'lucide-react';

interface ShareReferralCardProps {
  /** Código de influencer del cliente (si tiene). Si no, se usa el teléfono. */
  code?: string | null;
  /** Teléfono del cliente (identificador de referido por defecto). */
  phone?: string | null;
}

const VARIANTS: { key: ReferralCardVariant; label: string }[] = [
  { key: 'amigos', label: 'Amigable' },
  { key: 'elegante', label: 'Elegante' },
];

const ShareReferralCard: React.FC<ShareReferralCardProps> = ({ code, phone }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const referralsEnabled = currentUser?.referralsEnabled ?? false;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imgUrl, setImgUrl] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [variant, setVariant] = useState<ReferralCardVariant>('amigos');
  const [copied, setCopied] = useState(false);

  const businessName = currentUser?.businessName || 'Mi negocio';
  const primaryColor = currentUser?.businessColors?.primary || '#059669';
  const logoUrl = currentUser?.logoUrl || null;
  const loyaltyMode = currentUser?.loyaltyMode || 'visits';
  const pointsPerReferral = currentUser?.pointsPerReferral ?? 0;

  const rewardLabel = pointsPerReferral > 0
    ? (loyaltyMode === 'cashback' ? `$${pointsPerReferral}` : `${pointsPerReferral} puntos`)
    : null;

  const idText = (code && code.trim())
    ? `el código ${code.trim().toUpperCase()}`
    : (phone ? `el número ${phone}` : '');

  const shareText =
    `¡Te recomiendo ${businessName}! 🎁\n` +
    `Regístrate en la tienda y menciona ${idText} al dar tu número de teléfono` +
    (rewardLabel ? ` — ¡ambos ganamos ${rewardLabel}!` : '.');

  const generate = useCallback(async (v: ReferralCardVariant) => {
    setLoading(true);
    try {
      const b = await renderReferralCard({
        businessName, code, phone, rewardLabel, logoUrl, primaryColor, variant: v,
      });
      setBlob(b);
      setImgUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(b); });
    } catch (e) {
      console.error('Error generando tarjeta de referido:', e);
      toast.error('No se pudo generar la tarjeta');
    } finally {
      setLoading(false);
    }
  }, [businessName, code, phone, rewardLabel, logoUrl, primaryColor]);

  const handleOpen = () => {
    setOpen(true);
    void generate(variant);
  };

  const handleClose = () => {
    setOpen(false);
    if (imgUrl) { URL.revokeObjectURL(imgUrl); setImgUrl(null); }
    setBlob(null);
  };

  const handleVariant = (v: ReferralCardVariant) => {
    setVariant(v);
    void generate(v);
  };

  const handleShare = async () => {
    if (!blob) return;
    const file = new File([blob], 'invitacion.png', { type: 'image/png' });
    const nav = navigator as any;
    if (nav.canShare && nav.canShare({ files: [file] })) {
      try {
        await nav.share({ files: [file], text: shareText });
      } catch { /* usuario canceló */ }
    } else {
      handleDownload();
      toast('Imagen descargada — adjúntala en WhatsApp', { icon: '📎' });
    }
  };

  const handleDownload = () => {
    if (!imgUrl) return;
    const a = document.createElement('a');
    a.href = imgUrl;
    a.download = `invitacion-${(code || phone || 'referido')}.png`;
    a.click();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  return (
    <div className="mt-3">
      <button
        onClick={handleOpen}
        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-gradient-to-r from-emerald-600 to-cyan-500 hover:from-emerald-700 hover:to-cyan-600 transition-colors shadow-sm"
      >
        <Share2 className="h-3.5 w-3.5 mr-1.5" />
        Compartir invitación
      </button>

      {/* Aviso si los referidos están desactivados: la invitación NO sumará puntos */}
      {!referralsEnabled && (
        <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 dark:bg-amber-500/10 p-2 rounded-md border border-amber-200 dark:border-amber-500/30">
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
          <span>
            Los referidos están <strong>desactivados</strong>: esta invitación no sumará puntos hasta que los actives en{' '}
            <button onClick={() => navigate('/dashboard/referrals?tab=ajustes')} className="underline font-medium hover:text-amber-900 dark:hover:text-amber-200">
              Referidos → Ajustes
            </button>.
          </span>
        </div>
      )}

      {open && createPortal(
        <div className="modal-overlay fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[100]" onClick={handleClose}>
          <div
            className="modal-panel bg-surface rounded-2xl shadow-xl max-w-sm w-full flex flex-col max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header fijo (con botón cerrar) */}
            <div className="flex items-center justify-between gap-3 p-4 border-b border-line flex-shrink-0">
              <h3 className="text-base font-bold text-content">Compartir invitación</h3>
              <button
                onClick={handleClose}
                className="text-content-3 hover:text-content p-1.5 -mr-1.5 rounded-full hover:bg-surface-2 flex-shrink-0"
                aria-label="Cerrar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Cuerpo con scroll */}
            <div className="p-4 overflow-y-auto flex-1">
              <p className="text-xs text-content-3 mb-3">
                Manda esta tarjeta por WhatsApp. Tu cliente la enseña al registrarse en la tienda.
              </p>

              {/* Selector de estilo */}
              <div className="flex gap-2 mb-3">
                {VARIANTS.map((v) => (
                  <button
                    key={v.key}
                    onClick={() => handleVariant(v.key)}
                    disabled={loading}
                    className={`flex-1 text-xs font-medium py-1.5 rounded-md border transition-colors ${
                      variant === v.key
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'bg-surface-2 text-content-2 border-line hover:bg-surface'
                    }`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>

              {/* Preview (acotado en altura para que el footer siempre se vea) */}
              <div className="rounded-lg overflow-hidden border border-line bg-surface-2 flex items-center justify-center">
                {loading || !imgUrl ? (
                  <div className="h-44 flex items-center justify-center">
                    <Loader2 className="h-7 w-7 animate-spin text-emerald-500" />
                  </div>
                ) : (
                  <img src={imgUrl} alt="Tarjeta de invitación" className="w-full max-h-[44vh] object-contain" />
                )}
              </div>
            </div>

            {/* Footer fijo — acciones SIEMPRE visibles */}
            <div className="p-4 border-t border-line flex-shrink-0">
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={handleShare}
                  disabled={loading || !blob}
                  className="flex flex-col items-center justify-center gap-1 py-2.5 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                >
                  <Share2 className="h-4 w-4" /> Compartir
                </button>
                <button
                  onClick={handleDownload}
                  disabled={loading || !imgUrl}
                  className="flex flex-col items-center justify-center gap-1 py-2.5 rounded-lg bg-surface-2 border border-line text-content-2 text-xs font-medium hover:bg-surface disabled:opacity-50 transition-colors"
                >
                  <Download className="h-4 w-4" /> Descargar
                </button>
                <button
                  onClick={handleCopy}
                  className="flex flex-col items-center justify-center gap-1 py-2.5 rounded-lg bg-surface-2 border border-line text-content-2 text-xs font-medium hover:bg-surface transition-colors"
                >
                  {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                  {copied ? 'Copiado' : 'Copiar texto'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ShareReferralCard;
